import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { marketGoodsListFilterAtom } from '../states/GoodsListFilter.atom';
import { queryKey } from '../../../constant/queryKey';
import { useHasParentMarket } from './useHasParentMarket';
import {
  type GetMarketHomeGoodsRequestParams,
  marketService,
} from 'shared/src/api';
import type { GetMarketParentHomeGoodsRequestParams } from 'shared/src/api/marketService/getMarketParentHomeGoods/types';

export const usePrefetchMarketHomeGoods = (marketId: number) => {
  const filter = useAtomValue(marketGoodsListFilterAtom);
  const queryClient = useQueryClient();

  const { hasParentMarket } = useHasParentMarket(marketId);

  useEffect(() => {
    const filters = filter[marketId];

    if (hasParentMarket) {
      queryClient.prefetchInfiniteQuery({
        queryKey: queryKey.marketParentGoodsList(marketId, filters),
        queryFn: ({ pageParam }: { pageParam?: string | null }) => {
          const convertToRequestParams: () => GetMarketParentHomeGoodsRequestParams =
            () => {
              return {
                category_sno:
                  filters.subCategorySno ?? filters.categorySno ?? undefined,
                order: filters.sort,
                next_token: pageParam ?? undefined,
                delivery_type: filters.deliveryType ?? undefined,
                query: filters.keyword === '' ? undefined : filters.keyword,
              };
            };
          return marketService.getMarketParentHomeGoods(
            marketId,
            convertToRequestParams(),
          );
        },
        initialPageParam: undefined,
      });

      return;
    }

    queryClient.prefetchInfiniteQuery({
      queryKey: queryKey.marketGoodsList(marketId, filters),
      queryFn: ({ pageParam }: { pageParam?: string | null }) => {
        const convertToRequestParams: () => GetMarketHomeGoodsRequestParams =
          () => {
            return {
              category_sno:
                filters.subCategorySno ?? filters.categorySno ?? undefined,
              order: filters.sort,
              next_token: pageParam ?? undefined,
              delivery_type: filters.deliveryType ?? undefined,
              query: filters.keyword === '' ? undefined : filters.keyword,
            };
          };
        return marketService.getMarketHomeGoods(
          marketId,
          convertToRequestParams(),
        );
      },
      initialPageParam: undefined,
    });
  }, [marketId, filter, hasParentMarket, queryClient]);
};
